import React from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const FAQS = [
  {
    q: "Which classes do you offer?",
    a: "We teach BLS, ACLS, PALS, Heartsaver CPR/First Aid, and workplace safety programs. All five are treated equally — book any of them through our schedule page."
  },
  {
    q: "Are your certifications recognized?",
    a: "Our BLS, ACLS, PALS, and Heartsaver courses follow American Heart Association guidelines. Our workplace safety programs align with common OSHA training expectations. Certification cards and completion certificates are issued the same day."
  },
  {
    q: "Where are classes held?",
    a: "Classes are held in the DFW area (Dallas–Fort Worth metroplex). For group and workplace training, we come to your site."
  },
  {
    q: "How do I sign up?",
    a: "Head to the Schedule page, pick an available date for the class you want, fill out the registration form, and you'll get an on-screen confirmation with a code to save."
  },
  {
    q: "How do I pay?",
    a: "For open enrollment classes, payment is collected on-site. For group bookings, we send an invoice tied to your quote."
  },
  {
    q: "What's your cancellation policy?",
    a: "Full refunds are available up to 48 hours before class start. Later cancellations receive credit toward a future class. No-shows are non-refundable."
  },
  {
    q: "Do you offer renewals?",
    a: "Yes. Our ACLS and PALS renewals are shorter (4–5 hours) for currently certified providers. BLS is a full course for all attendees."
  },
  {
    q: "Can you train my whole team on-site?",
    a: "Absolutely. Visit our Group Training page or request a quote — we handle scheduling, instructors, and all equipment."
  },
  {
    q: "How many people do you fit per class?",
    a: "We keep group sizes small so everyone gets real hands-on practice. Exact caps vary by class and location."
  },
  {
    q: "Do I need any medical background for Heartsaver?",
    a: "No. Heartsaver CPR/AED/First Aid is designed for the general public — teachers, coaches, parents, coworkers. It's the right course for anyone without clinical training."
  },
];

export default function FAQs() {
  return (
    <div>
      <Seo
        title="FAQs"
        path="/faqs"
        description="Answers to the most common questions about our CPR, BLS, ACLS, PALS, Heartsaver, and workplace safety training in the DFW area."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
          <div className="font-mono-label text-[#d90429] mb-4">Frequently Asked Questions</div>
          <h1 className="font-display text-5xl md:text-6xl font-black text-[#0a0a0a] leading-none">
            Answers, up front.
          </h1>
          <p className="mt-6 text-lg text-gray-700 max-w-2xl">
            The most common questions we get about classes, certifications, and booking.
          </p>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Accordion type="single" collapsible className="bg-white border border-gray-200 rounded-lg px-6" data-testid="faqs-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-gray-200 last:border-b-0">
                <AccordionTrigger className="text-left font-display text-lg md:text-xl font-bold text-[#0a0a0a] hover:text-[#d90429] py-5" data-testid={`faq-question-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 text-base leading-relaxed pb-5" data-testid={`faq-answer-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center">
            <p className="text-gray-700">Still have questions?</p>
            <Link
              to="/contact"
              data-testid="faqs-contact-link"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#d90429] px-6 py-3 text-sm font-bold text-white hover:bg-black transition-colors"
            >
              Contact us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
